import { useState } from 'react';
import { Link } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import { Mail, Lock, User, Eye, EyeOff, CheckCircle, AlertTriangle, ArrowLeft, Loader2, Sun, Moon } from 'lucide-react';
import { useTheme } from '../../hooks/useTheme';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';


import { translateAuthError } from '../../utils/authErrors';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

export default function Signup() {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const { theme, toggleTheme } = useTheme();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.reset.error_match'));
      return;
    }

    if (formData.password.length < 6) {
      setError(t('auth.reset.error_length'));
      return;
    }

    setLoading(true);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.name,
          }
        }
      });

      if (error) throw error;

      if (data.user) {
        setSuccess(true);
        setTimeout(() => {
          window.REACT_APP_NAVIGATE('/dashboard');
        }, 1500);
      }
    } catch (err: any) {
      setError(translateAuthError(err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-dark-bg transition-colors duration-300 flex items-center justify-center px-4 py-12 relative">
      <SEO
        title={t('auth.signup.title')}
        description={t('auth.signup.seo_description')}
        canonical="/signup"
      />
      <div className="absolute top-4 right-4">

        <button
          onClick={toggleTheme}
          className="p-2.5 rounded-xl text-text-muted hover:bg-dark-hover hover:text-text-main transition-colors"
          title={theme === 'dark' ? t('dashboard.sidebar.mode_light') || 'Mode clair' : t('dashboard.sidebar.mode_dark') || 'Mode sombre'}
        >
          {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
        </button>
      </div>
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-3 mb-6">
            <div className="w-12 h-12 shrink-0">
              <img src="/logo/app-logo.png" alt="Logo" className="w-full h-full object-contain" />
            </div>
            <span className="text-3xl font-bold text-text-main">CitoyenPro</span>
          </Link>
          <h1 className="text-3xl font-bold text-text-main mb-2">{t('auth.signup.title')}</h1>
          <p className="text-text-muted">{t('auth.signup.subtitle')}</p>
        </div>

        {/* Signup Form */}
        <div className="bg-dark-card rounded-2xl border border-dark-border p-8 shadow-xl">
          {error && (
            <div className="mb-4 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
              <p className="text-sm text-red-500 flex items-center gap-2">
                <AlertTriangle size={16} />
                {error}
              </p>
            </div>
          )}

          {success && (
            <div className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-xl">
              <p className="text-sm text-green-500 flex items-center gap-2">
                <CheckCircle size={16} />
                {t('auth.signup.success')}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label htmlFor="name" className="block text-sm font-semibold text-text-main mb-2">
                {t('auth.signup.full_name')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <User className="text-text-muted" size={20} />
                </div>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full pl-12 pr-4 py-3 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-colors text-sm text-text-main placeholder-text-muted"
                  placeholder={t('auth.signup.full_name_placeholder')}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-semibold text-text-main mb-2">
                {t('auth.login.email')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail className="text-text-muted" size={20} />
                </div>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full pl-12 pr-4 py-3 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-colors text-sm text-text-main placeholder-text-muted"
                  placeholder={t('auth.login.email_placeholder')}
                  required
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-semibold text-text-main mb-2">
                {t('auth.login.password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="text-text-muted" size={20} />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  className="w-full pl-12 pr-12 py-3 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-colors text-sm text-text-main placeholder-text-muted"
                  placeholder={t('auth.login.password_placeholder')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer"
                >
                  {showPassword ? (
                    <EyeOff className="text-text-muted hover:text-text-main" size={20} />
                  ) : (
                    <Eye className="text-text-muted hover:text-text-main" size={20} />
                  )}
                </button>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-semibold text-text-main mb-2">
                {t('auth.reset.confirm_password')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock className="text-text-muted" size={20} />
                </div>
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  className="w-full pl-12 pr-12 py-3 bg-dark-bg border border-dark-border rounded-xl focus:border-primary focus:outline-none transition-colors text-sm text-text-main placeholder-text-muted"
                  placeholder={t('auth.login.password_placeholder')}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="text-text-muted hover:text-text-main" size={20} />
                  ) : (
                    <Eye className="text-text-muted hover:text-text-main" size={20} />
                  )}
                </button>
              </div>
            </div>

            <div className="flex items-start">
              <input type="checkbox" id="terms" className="w-4 h-4 mt-1 text-primary bg-dark-bg border-dark-border rounded focus:ring-primary cursor-pointer accent-primary" required />
              <label htmlFor="terms" className="ml-2 text-sm text-text-muted cursor-pointer">
                {t('auth.signup.terms_prefix')}{' '}
                <Link to="/legal/terms" className="text-primary hover:text-primary-hover font-medium">
                  {t('auth.signup.terms_link')}
                </Link>{' '}
                {t('auth.signup.terms_and')}{' '}
                <Link to="/legal/privacy" className="text-primary hover:text-primary-hover font-medium">
                  {t('auth.signup.privacy_link')}
                </Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-colors shadow-lg disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  {t('auth.signup.signing_up')}
                </>
              ) : (
                t('auth.signup.submit')
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-text-muted">
              {t('auth.signup.have_account')}{' '}
              <Link to="/login" className="text-text-main hover:text-primary font-semibold transition-colors">
                {t('auth.signup.login')}
              </Link>
            </p>
          </div>
        </div>

        {/* Back to home */}
        <div className="text-center mt-6">
          <Link to="/" className="text-text-muted hover:text-text-main text-sm flex items-center justify-center gap-2 transition-colors">
            <ArrowLeft size={16} />
            {t('auth.login.back_home')}
          </Link>
        </div>
      </div>
    </div>
  );
}
